'use client';

import React, { useEffect, useState, useCallback } from 'react';
import Badge from '@/components/ui/Badge';
import { fetchTopProducts } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

export default function TopProductsTable() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    fetchTopProducts(30, 8).then((result) => {
      if (result) setProducts(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();

    const supabase = createClient();
    const channel = supabase
      .channel('top-products-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, loadData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ad_campaigns' }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`sk-${i}`} className="h-8 bg-muted/40 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!products.length) {
    return <div className="p-6 text-center text-xs text-muted-foreground">No product data available</div>;
  }

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="text-left px-4 py-2.5 font-600 text-muted-foreground">Product</th>
            <th className="text-left px-3 py-2.5 font-600 text-muted-foreground">SKU</th>
            <th className="text-right px-3 py-2.5 font-600 text-muted-foreground">Units</th>
            <th className="text-right px-3 py-2.5 font-600 text-muted-foreground">Revenue</th>
            <th className="text-right px-3 py-2.5 font-600 text-muted-foreground">ROAS</th>
            <th className="text-right px-3 py-2.5 font-600 text-muted-foreground">Margin</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, idx) => (
            <tr key={p?.id} className={`border-b border-border/50 row-hover transition-colors cursor-pointer ${idx % 2 === 1 ? 'bg-muted/20' : ''}`}>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Badge variant={p?.brand === 'Lava' ? 'info' : 'warning'}>{p?.brand}</Badge>
                  <span className="font-500 text-foreground truncate max-w-40">{p?.name}</span>
                </div>
              </td>
              <td className="px-3 py-2.5 font-mono text-muted-foreground">{p?.sku}</td>
              <td className="px-3 py-2.5 text-right font-mono-nums font-600 text-foreground">{p?.units?.toLocaleString('en-IN')}</td>
              <td className="px-3 py-2.5 text-right font-mono-nums font-600 text-foreground">
                ₹{(p?.revenue / 100000)?.toFixed(2)}L
              </td>
              <td className="px-3 py-2.5 text-right">
                <span className={`font-mono-nums font-600 ${p?.roas >= 3 ? 'text-positive' : p?.roas >= 2 ? 'text-warning' : 'text-negative'}`}>
                  {p?.roas}×
                </span>
              </td>
              <td className="px-3 py-2.5 text-right">
                <span className={`font-mono-nums font-600 ${p?.margin >= 12 ? 'text-positive' : p?.margin >= 8 ? 'text-warning' : 'text-negative'}`}>
                  {p?.margin}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}